import dayjs from "dayjs";
import { MRT_SortingState } from "material-react-table";
import { euroToEurocent } from "@/helper/formattingValues";
import {
  isDurationFilterRangeValue,
  parseDurationFilterToMinutes,
} from "@/components/tables/genericCrudTableDurationFilterUtils";
import {
  isAnyDateRangeFilterValue,
  isDateRangeValueEmpty,
  matchesDateRangeClientSide,
  normalizeDateRangeValue,
} from "@/components/dateTime/genericDateFilterUtils";
import type { EntityAdapter } from "@/components/tables/entityAdapters/entityAdapter";



export const TABLE_COLS_SPECIAL_LEFT_ORDER = [
  "mrt-row-actions",
  "mrt-row-select",
];

export interface ColumnPinningState {
  left: string[];
  right: string[];
}

export interface PinningOptions {
  enableRowSelection?: boolean | ((row: any) => boolean);
  showRowActions?: boolean;
}

export const getColumnPinningFromColumns = (
  columns: any[],
): ColumnPinningState => {
  const leftPinnedColumns: string[] = [];
  const rightPinnedColumns: string[] = [];

  columns.forEach((column: any) => {
    if (column.pinColumnByDefault) {
      const location = column.pinnedColumnLocation || "left";
      if (location === "left") {
        leftPinnedColumns.push(column.id);
      } else if (location === "right") {
        rightPinnedColumns.push(column.id);
      }
    }
  });

  return { left: leftPinnedColumns, right: rightPinnedColumns };
};

const hasAnyNonSpecialPinned = (pinning: any): boolean => {
  const specials = new Set(TABLE_COLS_SPECIAL_LEFT_ORDER);
  return (
    (pinning.left ?? []).some((id: string) => !specials.has(id)) ||
    (pinning.right ?? []).some((id: string) => !specials.has(id))
  );
};

export const normalizePinning = (
  next: any,
  options: PinningOptions,
): ColumnPinningState => {
  const specials = new Set(TABLE_COLS_SPECIAL_LEFT_ORDER);

  const dedup = (arr: string[]) => Array.from(new Set(arr));
  const cleanLeft = dedup(
    (next.left ?? []).filter((id: string) => !specials.has(id)),
  );
  const cleanRight = dedup(
    (next.right ?? []).filter((id: string) => !specials.has(id)),
  );

  const userPinnedSomething = hasAnyNonSpecialPinned(next);

  const forcedSpecials: string[] = [];
  if (options.showRowActions !== false) forcedSpecials.push("mrt-row-actions");
  if (options.enableRowSelection) forcedSpecials.push("mrt-row-select");

  const left = userPinnedSomething
    ? [...forcedSpecials, ...cleanLeft]
    : cleanLeft;

  const right = cleanRight;

  return { left, right };
};

export interface DefaultSort {
  sort: string;
  tableSort: string;
  descending: boolean;
}

export const getDefaultSort = (entityAdapter?: EntityAdapter): DefaultSort =>
  entityAdapter?.defaultSort ?? {
    sort: "-created_on",
    tableSort: "created_on",
    descending: true,
  };

export const sanitizeValuesBeforeSendingToApi = (
  values: any,
  columns: any[],
) => {
  
  if (!values || typeof values !== "object") return values;
  const sanitized = { ...values };

  for (const [key, value] of Object.entries(sanitized)) {
    const column: any = columns.find((col: any) => col.id === key);

    if (!column) continue;

    const expectsJsonValue =
      column.isJson === true ||
      column.useJsonBuilder === true ||
      column.useJsonEditor === true;

    if (expectsJsonValue && typeof value === "string") {
      try {
        sanitized[key] = JSON.parse(value);
      } catch {
        //  content for catch
      }
    }

    if (Array.isArray(value)) {
      const isArrayOfObjectsJsonBuilderField =
        column.useJsonBuilder === true &&
        column.jsonBuilderProps?.isArrayOfObjects === true;

      if (isArrayOfObjectsJsonBuilderField) {
        const normalizedObjectEntries = value
          .map((item: any) => {
            if (!item || typeof item !== "object" || Array.isArray(item)) {
              return null;
            }

            return Object.entries(item).reduce(
              (acc: Record<string, any>, [entryKey, entryValue]) => {
                if (entryKey.trim() !== "") {
                  acc[entryKey] = entryValue;
                }
                return acc;
              },
              {},
            );
          })
          .filter(Boolean)
          .filter((item: any) => Object.keys(item).length > 0);

        const emptyArrayOfObjectsFallback =
          column.jsonBuilderProps?.emptyArrayOfObjectsValue ===
          "singleEmptyObject"
            ? [{}]
            : [];

        sanitized[key] =
          normalizedObjectEntries.length > 0
            ? normalizedObjectEntries
            : emptyArrayOfObjectsFallback;
        continue;
      }

      sanitized[key] = value.map((item: any) => {
        if (item && typeof item === "object" && !Array.isArray(item)) {
          const extracted = item.id ?? item.value;
          return extracted !== undefined && extracted !== null
            ? String(extracted)
            : item;
        }
        return String(item);
      });
    } else if (
      !expectsJsonValue &&
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      (Object.prototype.hasOwnProperty.call(value, "id") ||
        Object.prototype.hasOwnProperty.call(value, "value"))
    ) {
      const extracted = (value as any).id ?? (value as any).value;
      if (extracted !== undefined && extracted !== null) {
        sanitized[key] = String(extracted);
      }
    }
  }

  return sanitized;
};

export const normalizeRelationIdArray = (
  rawValue: any,
): Array<string | number> => {
  if (!Array.isArray(rawValue)) {
    return [];
  }

  return rawValue
    .map((item: any) => {
      if (item && typeof item === "object" && !Array.isArray(item)) {
        return item.id ?? item.value ?? null;
      }
      if (typeof item === "string" || typeof item === "number") {
        return item;
      }
      return null;
    })
    .filter(
      (id: string | number | null): id is string | number => id != null,
    );
};

export const normalizeIntegerIdArray = (rawValue: any): number[] => {
  if (!Array.isArray(rawValue)) {
    return [];
  }

  return rawValue
    .map((item: any) => {
      const rawId =
        item && typeof item === "object" && !Array.isArray(item)
          ? item.id ?? item.value
          : item;

      if (typeof rawId === "number" && Number.isFinite(rawId)) {
        return rawId;
      }

      if (typeof rawId === "string" && /^\d+$/.test(rawId.trim())) {
        return Number(rawId);
      }

      return null;
    })
    .filter((id: number | null): id is number => id != null);
};

export interface CustomSortOptions {
  columns: any[];
}

const getCustomTableDataValue = (row: any, accessorKey: string) => {
  if (!accessorKey.includes(".")) {
    return row?.[accessorKey];
  }

  return accessorKey
    .split(".")
    .reduce((value, key) => value?.[key], row);
};

export const sortCustomTableData = (
  data: any[],
  sortingState: MRT_SortingState,
  options: CustomSortOptions,
): any[] => {
  if (!sortingState || sortingState.length === 0) return data;

  return [...data].sort((a, b) => {
    for (const sort of sortingState) {
      const { id, desc } = sort;

      const columnDef = options.columns.find((col: any) => col.id === id);
      let aValue, bValue;

      if (columnDef) {
        const accessorKey = columnDef.accessorKey || id;
        aValue = getCustomTableDataValue(a, accessorKey);
        bValue = getCustomTableDataValue(b, accessorKey);
      } else {
        aValue = getCustomTableDataValue(a, id);
        bValue = getCustomTableDataValue(b, id);
      }

      if (aValue == null && bValue == null) continue;
      if (aValue == null) return desc ? 1 : -1;
      if (bValue == null) return desc ? -1 : 1;

      let comparison = 0;
      if (typeof aValue === "string" && typeof bValue === "string") {
        comparison = aValue.localeCompare(bValue);
      } else if (typeof aValue === "number" && typeof bValue === "number") {
        comparison = aValue - bValue;
      } else if (aValue instanceof Date && bValue instanceof Date) {
        comparison = aValue.getTime() - bValue.getTime();
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }

      if (comparison !== 0) {
        return desc ? -comparison : comparison;
      }
    }
    return 0;
  });
};

export interface CustomFilterOptions {
  columns: any[];
  entityAdapter?: EntityAdapter;
}

export const filterCustomTableData = (
  data: any[],
  columnFilters: any[],
  options: CustomFilterOptions,
): any[] => {
  if (!columnFilters || columnFilters.length === 0) return data;

  const shouldConvertPriceToEurocent =
    options.entityAdapter?.convertsPriceToEurocent ?? true;

  return data.filter((row) => {
    return columnFilters.every((filter) => {
      const { id, value } = filter;
      const columnDef = options.columns.find((col: any) => col.id === id);

      if (!columnDef) return true;

      const accessorKey = columnDef.accessorKey || id;
      const cellValue = getCustomTableDataValue(row, accessorKey);

      if (value === "" || value === null || value === undefined) return true;

      if (columnDef.filterVariant === "date") {
        if (isAnyDateRangeFilterValue(value)) {
          const range = normalizeDateRangeValue(value);
          if (isDateRangeValueEmpty(range)) return true;
          return matchesDateRangeClientSide(cellValue, range);
        }

        const filterDate = dayjs(value as dayjs.ConfigType);
        const cellDate = dayjs(cellValue as dayjs.ConfigType);

        if (!filterDate.isValid() || !cellDate.isValid()) {
          return false;
        }

        return cellDate.isSame(filterDate, "day");
      }

      if (columnDef.filterVariant === "checkbox") {
        return String(cellValue) === String(value);
      }

      if (columnDef.filterVariant === "price") {
        if (options.entityAdapter?.matchesPriceFilterClientSide) {
          return options.entityAdapter.matchesPriceFilterClientSide(
            cellValue,
            value
          );
        }
        const normalized = value.toString().replace(",", ".");
        const filterPrice = shouldConvertPriceToEurocent
          ? euroToEurocent(normalized)
          : Number(normalized);
        return String(cellValue) === String(filterPrice);
      }

      if (columnDef.filterVariant === "duration") {
        if (isDurationFilterRangeValue(value)) {
          const cellDuration = Number(cellValue);
          const fromDuration = Number(
            parseDurationFilterToMinutes({
              amount: value.from,
              unit: value.unit,
            }),
          );
          const toDuration = Number(
            parseDurationFilterToMinutes({
              amount: value.to,
              unit: value.unit,
            }),
          );

          if (!Number.isFinite(cellDuration)) return false;
          const hasFrom = Number.isFinite(fromDuration);
          const hasTo = Number.isFinite(toDuration);
          if (!hasFrom && !hasTo) return true;

          const rangeStart = hasFrom && hasTo
            ? Math.min(fromDuration, toDuration)
            : fromDuration;
          const rangeEnd = hasFrom && hasTo
            ? Math.max(fromDuration, toDuration)
            : toDuration;

          if (Number.isFinite(rangeStart) && cellDuration < rangeStart) {
            return false;
          }
          if (Number.isFinite(rangeEnd) && cellDuration > rangeEnd) {
            return false;
          }
          return true;
        }

        const filterDuration = parseDurationFilterToMinutes(value);
        if (filterDuration === undefined) return true;
        return String(cellValue) === filterDuration;
      }

      if (cellValue == null) return false;

      const cellValueStr = String(cellValue).toLowerCase();
      const filterValueStr = String(value).toLowerCase();

      return cellValueStr.includes(filterValueStr);
    });
  });
};
